import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { useAdminAuth } from "@/admin/AuthContext";
import {
  ChartBar, UsersThree, EnvelopeSimple, Target,
  ChatTeardropText, SignOut, House, ClipboardText, Images,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/admin", end: true, label: "Overview", icon: ChartBar },
  { to: "/admin/leads", label: "Lead Finder", icon: Target },
  { to: "/admin/campaigns", label: "Campaigns", icon: EnvelopeSimple },
  { to: "/admin/templates", label: "Templates", icon: ClipboardText },
  { to: "/admin/contacts", label: "Contacts", icon: ChatTeardropText },
  { to: "/admin/free-trials", label: "Free Trials", icon: UsersThree },
  { to: "/admin/portfolio", label: "Portfolio", icon: Images },
];

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "cad4surveys | Admin";
  }, []);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 text-zinc-500 font-mono text-xs">
        Loading admin…
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen grid" style={{ gridTemplateColumns: "260px 1fr" }} data-testid="admin-layout">
      <aside className="bg-zinc-950 text-zinc-300 border-r border-zinc-800 flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 border border-zinc-600 flex items-center justify-center font-mono text-[10px] font-bold bg-zinc-900 text-white">
              C4S
            </div>
            <div>
              <div className="font-display text-white text-sm font-medium">cad4surveys</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">admin</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1" aria-label="Admin">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-[#0044FF] text-white"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-400 hover:text-white hover:bg-zinc-900"
          >
            <House size={18} weight="duotone" />
            View Site
          </a>
          <button
            onClick={() => {
              logout();
              navigate("/admin/login");
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-400 hover:text-white hover:bg-zinc-900"
            data-testid="admin-logout"
          >
            <SignOut size={18} weight="duotone" />
            Sign out
          </button>
          <div className="px-3 pt-2 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-600 truncate">
            {user?.email}
          </div>
        </div>
      </aside>

      <main className="bg-zinc-50 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
